import React from 'react';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import {duckify, filterDuck, singleDuck} from 'utils/duckify';
import {
  billingLedgerItems,
  modals,
  schoolYears,
  fields as fieldsDuck,
  billingCategoryGroups as billingCategories,
  grades, customers as customersDuck
} from 'ducks';
import withPagination from 'hocs/withPagination';
import { titleize } from 'utils/strings';
import { Link, withRouter } from 'react-router';
import { compose } from 'redux';
import queryString from 'query-string';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';
import Filters from 'components/Filters/Filters';

import './styles/LedgerItemList.scss';

import withFiltering from 'hocs/withFiltering';
import ContactList from 'components/ContactList/ContactList';
import DateFilter from '../shared/DateFilter';
import Placeholder from '../Placeholder';
import withDateFilter from '../../hocs/withDateFilter';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';
import BulkActions from '../ContactList/BulkActions/BulkActions';
import BulkAction from '../ContactList/BulkActions/BulkAction';
import {
  currencyColumn,
  dateColumn,
  defaultColumn,
  expanderColumn,
  linkColumn
} from '../ContactList/ColumnDefinitions';
import withSchoolYear, {defaultBillingSchoolYear} from "../../hocs/withSchoolYear";

const LedgerItemList = ({
  billingCategories,
  billingLedgerItems,
  customer,
  filters,
  additionalFields,
  page,
  schoolYears,
  grades,
  setFilters,
  setPage,
  dateFilter,
  location
}) => {
  const { total, perPage } = billingLedgerItems;
  const totalPages = Math.ceil(total / perPage);
  const paymentMethods = Object.values(customer.payment_config.payment_methods)
    .map(m => [m.display_name, m.canonical_name]);
  paymentMethods.sort();

  // options include the group (charge) category and each child (credit) category
  const billingCategoryOptions = billingCategories.map((group) => (
    [
      [
        `${group.name}${group.active ? '' : ' (inactive)'}`,
        group.id
      ]
    ].concat(
      group.children.map((category) => (
        [
          `${category.name}${category.active ? '' : ' (inactive)'}`,
          category.id
        ]
      ))
    )
  )).flat();

  let additionalColumns = [];

  if (billingLedgerItems.ready) {
    additionalColumns = billingLedgerItems.additionalFields.map(column => ({
      header: column.print_name,
      value: (item) => (
        <td
          className='contact-list-react-type'
          dangerouslySetInnerHTML={{ __html: item.additional_fields && item.additional_fields[column.canonical_name] }}
        />
      )
    }));
  }

  const schoolYearItems = schoolYears.map(y => [y.name, y.id]);
  schoolYearItems.push(['no term', 'no-term']);

  const filterFields = [
    {
      solr_prompt: 'school_year_id',
      display_name: 'Term',
      options: schoolYearItems
    },
    {
      solr_prompt: 'enroll_grade_id',
      display_name: 'Grade',
      options: grades.map(g => [g.name, g.id])
    },
    {
      solr_prompt: 'type',
      display_name: 'Type',
      options: [['Charge', 'charge'], ['Credit', 'credit'], ['Payment', 'payment']]
    },
    {
      display_name: 'Payment Type',
      solr_prompt: 'payment_type',
      options: [['Manual Payments', 'manual']]
    },
    {
      solr_prompt: 'billing_category_id',
      display_name: 'Category',
      options: billingCategoryOptions
    },
    {
      solr_prompt: 'billing_category_parent_id',
      display_name: 'Category Group',
      options: billingCategories.map((category) => [category.name, category.id])
    },
    {
      solr_prompt: 'payment_method',
      display_name: 'Payment Method',
      options: paymentMethods
    },
    {
      filter_type: 'TextFilter',
      solr_prompt: 'first_name',
      display_name: 'Account First Name'
    },
    {
      filter_type: 'TextFilter',
      solr_prompt: 'last_name',
      display_name: 'Account Last Name'
    }
  ];

  /** The list of columns to show for this page */
  const columns = [
    expanderColumn({}),
    dateColumn({
      header: 'Posted Date',
      type: 'date',
      value: 'billing_date'
    }),
    linkColumn({
      header: 'Student Name',
      value: (item) => item.student_name,
      link: (item) => item.student_name_link
    }),
    defaultColumn({
      header: 'Term',
      value: (item) => (
        <td>{item.term}</td>
      )
    }),
    linkColumn({
      header: 'Associated Account',
      value: (item) => `${item.associated_account_first_name} ${item.associated_account_last_name}`,
      link: (item) => `${item.associated_account_link}`
    }),
    currencyColumn({
      header: 'Amount',
      defaultToZero: true,
      value: 'amount'
    }),
    defaultColumn({
      header: 'Description',
      value: 'description'
    }),
    defaultColumn({
      header: 'Category',
      value: (item) => (
        <td>{item.billing_category_name}</td>
      )
    }),
    defaultColumn({
      header: 'Type',
      value: (item) => (
        <td>{titleize(item.type)}</td>
      )
    }),
    defaultColumn({
      header: 'Payment Method',
      value: 'payment_method_display_name'
    }),
    defaultColumn({
      header: 'Received Date',
      type: 'date',
      value: 'received_date'
    }),
    ...additionalColumns
  ];

  return (
    <div key={JSON.stringify({
      filters, billingLedgerItems, schoolYears, billingCategories, page
    })}
    >
      <div className='ledger-title'>
        <div className='ledger-space-between'>
          <h2>Ledger Report</h2>
          <BulkActions>
            <BulkAction title='Export' icon={faSignOut}>
              <BulkExportAction
                type='LedgerItemCsvJob'
                location={location}
                dateFilter={dateFilter}
                url='/api/billing_ledger_items/export.csv'
              />
              <BulkExportAction
                title='Export to PDF'
                type='LedgerItemCsvJob'
                location={location}
                dateFilter={dateFilter}
                url='/api/billing_ledger_items/export.pdf'
              />
            </BulkAction>
          </BulkActions>
        </div>

        <p>
          Your Ledger Report downloads will be available&nbsp;
          <Link to='/setup/user_jobs'>here</Link>
          .
        </p>

        <DateFilter label={'Posted Date Range'} dateFilter={dateFilter} />

        <div className='ledger-space-between'>
          <Filters
            fields={filterFields.concat(additionalFields)}
            count={billingLedgerItems.ready ? billingLedgerItems.total : null}
            selected={filters}
            submitFilters={setFilters}
          />
          {totalPages > 1 && billingLedgerItems.ready && (
            <div>
              <div className='contact-list-react-pagination'>
                <div className='pagination pagination-right'>
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    hideFirstAndLastPageLinks
                    onChange={setPage}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Placeholder.invisible ready={billingLedgerItems.ready}>
        <LedgerItemTable billingLedgerItems={billingLedgerItems} customer={customer} />
      </Placeholder.invisible>

      <ContactList
        className={'ledger-item-list'}
        columns={columns}
        contacts={billingLedgerItems}
        emptyMessage='There are no ledger items for this year.'
        getSubRowContacts={(contact) => contact.billing_item_amounts}
      />
    </div>
  );
};

const LedgerItemTable = ({ billingLedgerItems, customer }) => (
  <table className='table' />
);

const mapDucksToProps = ({page, location, dateFilter}) => {
  return {
    customer: singleDuck(customersDuck, 'current'),
    billingLedgerItems: filterDuck(billingLedgerItems, {
      ...dateFilter.queryParams,
      ...queryString.parse(location.search),
      page: page || 1
    }),
    additionalFields: filterDuck(fieldsDuck, {for: 'billing'}),
    billingCategories,
    grades,
    schoolYears
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  ...props,
  setFilters: (newFilters) => {
    props.setFilters(newFilters);
    dispatch(billingLedgerItems.actions.markDirty());
  }
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withDateFilter(),
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps),
  withSchoolYear(defaultBillingSchoolYear),
)(LedgerItemList);
